import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  useGetShopCategoriesQuery,
  useCreateShopCategoryMutation,
  useUpdateShopCategoryMutation,
  useDeleteShopCategoryMutation,
  ShopCategory,
} from '../store/services/shopCategories';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
});

interface CategoryFormValues {
  name: string;
}

export default function ShopCategories() {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedCategory, setSelectedCategory] = useState<ShopCategory | null>(null);
  
  // Get the current user from Redux state
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  // Check if user has permission to add/edit/delete
  const hasAdminPermission = currentUser?.role === 'SuperAdmin';
  
  const { data: categories, isLoading, refetch } = useGetShopCategoriesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  
  const [createCategory] = useCreateShopCategoryMutation();
  const [updateCategory] = useUpdateShopCategoryMutation();
  const [deleteCategory] = useDeleteShopCategoryMutation();

  const formik = useFormik<CategoryFormValues>({
    initialValues: {
      name: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedCategory) {
          await updateCategory({
            id: selectedCategory._id,
            category: values,
          }).unwrap();
          setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
        } else {
          await createCategory(values).unwrap();
          setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
        }
        handleClose();
        refetch(); // Force a refetch after mutation
      } catch (error) {
        console.error('Failed to save category:', error);
        setSnackbar({ open: true, message: 'Failed to save category', severity: 'error' });
      }
    },
  });

  const handleClickOpen = () => {
    setSelectedCategory(null);
    formik.resetForm();
    setOpen(true);
  };

  const handleEdit = (category: ShopCategory) => {
    setSelectedCategory(category);
    formik.setValues({
      name: category.name,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(id).unwrap();
        setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
        refetch(); // Force a refetch after deletion
      } catch (error) {
        console.error('Failed to delete category:', error);
        setSnackbar({ open: true, message: 'Failed to delete category', severity: 'error' });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Shop Categories</h2>
        {hasAdminPermission && (
          <Button variant="contained" onClick={handleClickOpen}>
            Add Category
          </Button>
        )}
      </Box>

      {!hasAdminPermission && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to shop categories.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Updated At</TableCell>
              {hasAdminPermission && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories?.map((category) => (
              <TableRow key={category._id}>
                <TableCell>{category.name}</TableCell>
                <TableCell>{format(new Date(category.created_at), 'PPpp')}</TableCell>
                <TableCell>{format(new Date(category.updated_at), 'PPpp')}</TableCell>
                {hasAdminPermission && (
                  <TableCell align="right">
                    <IconButton onClick={() => handleEdit(category)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(category._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}